import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { TemplatePreview, type PreviewPayload } from '../../components/inbox/template-preview';
import {
  IgBuilderSwitch,
  KIND_META,
  type TemplateKind,
} from '../../components/templates/instagram-template-builders';

interface LoadedTemplate {
  uid: string;
  name: string;
  kind: string;
  payload?: unknown;
}

/** The form — mounted only once the template has loaded, so its builder state
 *  can be seeded from the stored payload via `initial`. */
function EditForm({ tpl }: { tpl: LoadedTemplate }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const kind = tpl.kind as TemplateKind;
  const [name, setName] = useState(tpl.name);
  const [preview, setPreview] = useState<PreviewPayload>({});

  const update = trpc.instagram.templates.update.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Template updated', color: 'green' });
      void utils.instagram.templates.list.invalidate();
      void utils.instagram.templates.get.invalidate({ uid: tpl.uid });
      navigate({ to: '/vendor/instagram/templates' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const nameError = !name.trim() ? 'Give the template a name' : undefined;

  function handleSave(payload: unknown) {
    if (!name.trim()) {
      notifications.show({ message: 'Give the template a name', color: 'yellow' });
      return;
    }
    update.mutate({ uid: tpl.uid, name: name.trim(), payload } as Parameters<
      typeof update.mutate
    >[0]);
  }

  return (
    <Grid gap="md">
      <Grid.Col span={{ base: 12, md: 7 }}>
        <Card>
          <Stack gap="sm">
            <Group grow>
              <TextInput
                label="Template name"
                value={name}
                error={nameError}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <TextInput label="Template type" value={KIND_META[kind]?.label ?? kind} disabled />
            </Group>
            <Divider />
            <IgBuilderSwitch
              kind={kind}
              sending={update.isPending}
              submitLabel="Save changes"
              initial={tpl.payload}
              onChange={setPreview}
              onSave={handleSave}
            />
          </Stack>
        </Card>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 5 }}>
        <Stack gap="xs" pos="sticky" top={80}>
          <Text size="sm" fw={500}>
            Live preview
          </Text>
          <Box>
            <TemplatePreview kind={kind} payload={preview} channel="instagram" />
          </Box>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}

function EditInstagramTemplate() {
  const { uid } = Route.useParams();
  const tpl = trpc.instagram.templates.get.useQuery({ uid });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            component={Link}
            to="/vendor/instagram/templates"
            leftSection={<IconArrowLeft size={14} />}
          >
            Instagram templates
          </Button>
        </Group>
        <Title order={2}>Edit Instagram template</Title>
        {tpl.isPending ? (
          <Skeleton height={320} />
        ) : tpl.isError ? (
          <Text c="red">{tpl.error.message}</Text>
        ) : (
          <EditForm tpl={tpl.data} />
        )}
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/instagram/templates/$uid/edit')({
  component: EditInstagramTemplate,
});
